import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import Editor from "@monaco-editor/react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import { FRONTEND_DEVELOPER_ROADMAP } from "../roadmaps-data/frontend-developer";
import { REACT_NATIVE_DEVELOPER_ROADMAP } from "../roadmaps-data/react-native-developer";

/* ================= ROADMAP MAP ================= */

const ROADMAP_MAP = {
  frontend: FRONTEND_DEVELOPER_ROADMAP,
  mobile: REACT_NATIVE_DEVELOPER_ROADMAP,
};

/* ================= LAYOUT ================= */

const Page = styled.div`
  height: 100vh;
  width: 100%;
  background: ${({ theme }) => theme.colors.bg};
  display: grid;
  grid-template-rows: 64px 48px 1fr;
`;

const Header = styled.div`
  padding: 16px 24px;
  font-weight: 900;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgElevated};
  color: ${({ theme }) => theme.colors.text};
`;

const Toolbar = styled.div`
  padding: 10px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewToggle = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ active }) =>
    active ? "rgba(124,58,237,0.18)" : "transparent"};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const RunButton = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  font-weight: 900;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.accent}
  );
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Preview = styled.iframe.attrs({
  sandbox: "allow-scripts",
})`
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
`;

/* ================= COMPONENT ================= */

export default function EditorPage() {
  const { user } = useAuth();
  const [view, setView] = useState("editor");

  const params = new URLSearchParams(window.location.search);
  const weekNo = Number(params.get("week"));
  const dayNo = Number(params.get("day"));

  const [code, setCode] = useState("");
  const [topic, setTopic] = useState("");
  const [srcDoc, setSrcDoc] = useState("");
  const [roadmap, setRoadmap] = useState(null);

  /* ================= LOAD ROLE & ROADMAP ================= */

  useEffect(() => {
    if (!user) return;

    const loadRoadmap = async () => {
      const snap = await getDoc(doc(db, "roadmaps", user.uid));
      if (!snap.exists()) return;

      const role = snap.data()?.direction?.role;
      const activeRoadmap = ROADMAP_MAP[role];

      if (!activeRoadmap) return;

      setRoadmap(activeRoadmap);
    };

    loadRoadmap();
  }, [user]);

  /* ================= LOAD DAY CONTENT ================= */

  useEffect(() => {
    if (!roadmap || !weekNo || !dayNo) return;

    const phase = roadmap.find((p) => p.weeks?.some((w) => w.week === weekNo));
    if (!phase) return;

    const week = phase.weeks.find((w) => w.week === weekNo);
    if (!week) return;

    const day = week.days.find((d) => d.day === dayNo);
    if (!day) return;

    setTopic(day.topic);

    setCode(
      `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <title>${day.topic}</title>
    <style>
      body {
        font-family: system-ui, sans-serif;
        padding: 20px;
      }
    </style>
  </head>
  <body>

    ${day.examples?.join("\n") || "<h1>Hello Skilll.dev</h1>"}

    <script>
      // write JavaScript here
    </script>
  </body>
</html>
`.trim()
    );
  }, [roadmap, weekNo, dayNo]);

  const runCode = () => setSrcDoc(code);

  return (
    <Page>
      <Header>
        Day {dayNo} · {topic || "Loading..."}
      </Header>

      <Toolbar>
        <span>Write & experiment freely 👇</span>

        <div style={{ display: "flex", gap: 8 }}>
          <ViewToggle
            active={view === "editor"}
            onClick={() => setView("editor")}
          >
            Editor
          </ViewToggle>
          <ViewToggle
            active={view === "preview"}
            onClick={() => setView("preview")}
          >
            Preview
          </ViewToggle>
          <RunButton onClick={runCode}>▶ Run</RunButton>
        </div>
      </Toolbar>

      <Body>
        {(view === "editor" || window.innerWidth > 768) && (
          <Editor
            height="100%"
            language="html"
            value={code}
            onChange={(v) => setCode(v || "")}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "on",
              automaticLayout: true,
            }}
          />
        )}

        {(view === "preview" || window.innerWidth > 768) && (
          <Preview srcDoc={srcDoc} />
        )}
      </Body>
    </Page>
  );
}
